/**
 * Command palette v2 — the JS provider interface (PLAN §12.1, binding).
 *
 * The repo is JavaScript, so the contract lives here as JSDoc typedefs. They are
 * the §12.1 TypeScript block, verbatim in meaning; any change goes through
 * PLAN.md §12 first (R10). This module exports nothing at runtime except the
 * kind and range helpers below, which only restate the contract.
 *
 * @typedef {string} ProviderId            'tasks', 'people', … unique, [a-z-]+
 * @typedef {string} Ref                   `${ProviderId}:${string}` — frecency / recent key, stable across sessions
 * @typedef {'local-sync'|'local-ipc'|'server'} Kind
 *
 * @typedef {Object} PaletteCtx
 * @property {'desktop'|'web'} platform
 * @property {boolean} signedIn            false ⇒ palette never opens (D5)
 * @property {boolean} online
 * @property {string} activeTab            e.g. 'documents.panel'
 * @property {string[]} scopes             record scopes the user may search NOW:
 *                                         personalScopeId(), deviceScopeId() if any, 'team:<id>' per teamsStore.teams
 * @property {string[]} teamIds
 * @property {string|null} currentWorkspaceId   API workspace
 * @property {string} activeProjectId      a project id | 'all' | 'none'
 * @property {boolean} vaultUnlocked
 * @property {string|null} currentUserId
 * @property {(key: string, opts?: object) => string} t
 * @property {NavApi} nav                  the §3.5 open functions, injected (testable)
 *
 * @typedef {Object<string, Function>} NavApi
 *
 * @typedef {Object} SearchRequest
 * @property {string} text                 query text after the prefix/chip, trimmed
 * @property {string[]} terms              tokenizeQuery(text) — chat-search tokenizer (quoted phrases)
 * @property {Object<string, string[]>} filters   in-scope tokens (S9; empty this release)
 * @property {boolean} scoped              true when this provider's chip is active
 * @property {number} limit                6 in global mode (5 shown + "more" detection), 200 scoped
 * @property {AbortSignal} signal          aborted on the next keystroke / close
 * @property {PaletteCtx} ctx
 *
 * @typedef {Result[]|Promise<Result[]>|AsyncIterable<Result[]>} SearchOutput
 *
 * @typedef {Object} Provider
 * @property {ProviderId} id
 * @property {string[]} prefixes           lower-case words without ':'; [0] is canonical
 * @property {string[]} [sigils]           single chars: '>', '@', '#', '?'
 * @property {string} labelKey             i18n key: chip + group header
 * @property {Function} icon               lucide icon component
 * @property {number} order                group order (§2.4), lower first
 * @property {string} [page]               panel id for the Pages row
 * @property {Kind} kind
 * @property {number} [debounceMs]         default: local-sync 0, local-ipc 30, server 200
 * @property {number} [minChars]           default: 0 local, 2 server (messages: 3)
 * @property {boolean} [inGlobal]          default true; false = only when scoped
 * @property {(ctx: PaletteCtx) => boolean} available
 * @property {(req: SearchRequest) => SearchOutput} search
 * @property {(ids: string[], ctx: PaletteCtx) => Promise<Result[]>} [recent]   hydrate frecency refs of this provider
 * @property {(result: Result, ctx: PaletteCtx) => Action[]} actions            [0] = Enter default
 * @property {() => void} [dispose]
 *
 * @typedef {Object} Range                 UTF-16 code units, end exclusive
 * @property {number} start
 * @property {number} end
 *
 * @typedef {Object} Result
 * @property {ProviderId} provider
 * @property {string} id                   unique within provider
 * @property {string} title
 * @property {string} [subtitle]           path, board › list, e-mail, conversation …
 * @property {string} [keyLabel]           task key 'WEB-4' (searchable, shown right)
 * @property {string} [badge]              'Team Web', workspace name
 * @property {Function} [icon]
 * @property {string} [avatarUserId]       people / message sender
 * @property {string} [method]             HTTP method for requests
 * @property {number} [ts]                 epoch ms (updated_at, message time) for tie-break / display
 * @property {{text: string, ranges: Range[]}} [snippet]   body / message text with server/Rust-computed hits
 * @property {number} [baseScore]          0..1 provider relevance (bm25/server); used when title does not match
 * @property {{text: string, weight: number}[]} [searchFields]   extra fields the scorer may match (keywords, e-mail)
 * @property {*} payload                   whatever actions need
 * @property {number} [score]              set by the orchestrator, never by providers
 * @property {{field: 'title'|'subtitle'|'keyLabel', ranges: Range[]}[]} [matches]   set by the orchestrator
 *
 * @typedef {Object} Action
 * @property {string} id                   'open', 'openNewTab', 'copyLink', 'copyTaskKey', …
 * @property {string} titleKey
 * @property {Function} [icon]
 * @property {{mod?: boolean, shift?: boolean, alt?: boolean, key: string}} [shortcut]
 * @property {(ctx: PaletteCtx) => boolean} [when]
 * @property {(result: Result, ctx: PaletteCtx, opts: {newTab: boolean}) => (void|Promise<void>)} run
 * @property {boolean} [keepOpen]          default false: the palette closes before run()
 *
 * @typedef {Object} Command
 * @property {string} id
 * @property {string} titleKey
 * @property {string[]} [keywords]
 * @property {Function} [icon]
 * @property {string} [shortcutId]         config/shortcuts.js id → label + remap
 * @property {'suggested'|'general'|string} [group]
 * @property {(ctx: PaletteCtx) => boolean} [when]
 * @property {(ctx: PaletteCtx) => (void|Promise<void>)} run
 */

/** The three provider kinds of §12.1. */
export const KINDS = Object.freeze(['local-sync', 'local-ipc', 'server']);

/** §12.1 defaults per kind, when a provider does not set them. */
export const KIND_DEFAULTS = Object.freeze({
  'local-sync': Object.freeze({ debounceMs: 0, minChars: 0 }),
  'local-ipc': Object.freeze({ debounceMs: 30, minChars: 0 }),
  server: Object.freeze({ debounceMs: 200, minChars: 2 }),
});

/** §12.1 limits: 6 in global mode (5 shown + "more" detection), 200 scoped. */
export const GLOBAL_LIMIT = 6;
export const GLOBAL_SHOWN = 5;
export const SCOPED_LIMIT = 200;

/** `${provider}:${id}` */
export function refOf(provider, id) {
  return `${provider}:${id}`;
}

/** Splits a Ref at its FIRST colon (ids may contain colons, e.g. 'team:7'). */
export function parseRef(ref) {
  const s = String(ref || '');
  const i = s.indexOf(':');
  if (i <= 0) return null;
  return { provider: s.slice(0, i), id: s.slice(i + 1) };
}

/** An `AbortError`, the one error a provider may throw silently (§12.1 rule 4). */
export function abortError() {
  if (typeof DOMException === 'function') {
    try {
      return new DOMException('Aborted', 'AbortError');
    } catch {
      /* fall through */
    }
  }
  const e = new Error('Aborted');
  e.name = 'AbortError';
  return e;
}

export function isAbortError(err) {
  return !!err && err.name === 'AbortError';
}
